/**
 * Single source of truth for "can this WhatsApp number send messages?" on the client.
 *
 * Meta exposes several status-ish fields on a business phone number and only one
 * of them governs messaging. From the Cloud API docs: "Business phone numbers must
 * have a status of connected in order to send and receive messages via the API."
 *
 * `codeVerificationStatus` in particular does NOT gate sending. It records whether
 * the ownership OTP captured during registration is still on file, and Meta lets it
 * lapse to NOT_VERIFIED/EXPIRED on numbers that are connected and sending normally.
 * Filtering pickers on it removed healthy numbers from the whole app while the
 * backend happily kept sending from them.
 *
 * It may be read to explain WHY an already-unsendable number is unsendable, and to
 * decide which setup step a number still owes us — see `phoneSetupStep`. It may never
 * decide `canSend`. `evaluateSendability` enforces that by ordering: the sendable
 * statuses return before the field is looked at, so no value of it can turn a
 * sendable number unsendable. That ordering is load-bearing; do not reshuffle it.
 *
 * Mirrors `functions/src/services/whatsapp/phone-capability.ts`. Keep the two in step.
 */

/** Meta platform statuses from which a number can originate messages. */
export const SENDABLE_STATUSES = ['CONNECTED', 'FLAGGED'] as const;

/**
 * Sendable in principle, but Meta is refusing right now. RESTRICTED means the
 * 24-hour messaging limit is spent; RATE_LIMITED means throughput is capped.
 */
export const TEMPORARILY_BLOCKED_STATUSES = ['RESTRICTED', 'RATE_LIMITED'] as const;

export type SendabilityCode =
  | 'needs_verification'
  | 'not_registered'
  | 'rate_limited'
  | 'disconnected'
  | 'unknown_status';

export interface PhoneSendability {
  canSend: boolean;
  /** Machine-readable reason when canSend is false. */
  code?: SendabilityCode;
  /** Human-readable reason, safe to render directly. */
  reason?: string;
  /** True when the block is expected to clear on its own (limit windows). */
  temporary?: boolean;
}

/** The subset of a phone record this module reads. */
export interface SendablePhoneLike {
  status?: string | null;
  isConnected?: boolean | null;
  /**
   * Read only to explain an already-unsendable number and to pick its next setup
   * step. Never to decide `canSend` — see the module header.
   */
  codeVerificationStatus?: string | null;
  /**
   * True when this number is shared with the customer's WhatsApp Business app
   * (Coexistence). Such a number is registered by definition, so it owes no
   * setup call — see phoneSetupStep.
   */
  isOnBizApp?: boolean | null;
}

/**
 * Has Meta's ownership OTP been completed for this number?
 *
 * Absent means unknown rather than unverified: records written before the field was
 * fetched carry no value, and reporting those as "needs an OTP" would send people to
 * re-verify numbers that are already live.
 */
function isCodeVerified(phone: SendablePhoneLike): boolean {
  const value = typeof phone.codeVerificationStatus === 'string'
    ? phone.codeVerificationStatus.toUpperCase()
    : '';
  return value !== 'NOT_VERIFIED' && value !== 'EXPIRED';
}

/**
 * Decide whether a phone record may be used as a sending number.
 */
export function evaluateSendability(phone: SendablePhoneLike | null | undefined): PhoneSendability {
  if (!phone) {
    return {
      canSend: false,
      code: 'unknown_status',
      reason: 'No WhatsApp number record was found.'
    };
  }

  const status = typeof phone.status === 'string' ? phone.status.toUpperCase() : '';

  // Records written before `status` was stored only carry the derived boolean.
  if (!status) {
    return phone.isConnected === true
      ? { canSend: true }
      : {
        canSend: false,
        code: 'not_registered',
        reason: 'This number is not registered with WhatsApp yet.'
      };
  }

  // Before any look at codeVerificationStatus — see the module header.
  if ((SENDABLE_STATUSES as readonly string[]).includes(status)) {
    return { canSend: true };
  }

  if (status === 'RESTRICTED') {
    return {
      canSend: false,
      code: 'rate_limited',
      temporary: true,
      reason: 'This number has reached its 24-hour messaging limit. ' +
        'Sending resumes automatically once the window rolls over.'
    };
  }

  if (status === 'RATE_LIMITED') {
    return {
      canSend: false,
      code: 'rate_limited',
      temporary: true,
      reason: 'Meta is currently rate limiting this number. Try again shortly.'
    };
  }

  // Two distinct states used to collapse into one message. A number that has not
  // completed Meta's ownership OTP cannot be registered at all: POST /register
  // rejects it with a bare "(#100) Invalid parameter", which told a migrating
  // customer nothing about the step they had actually missed.
  if (status === 'PENDING' || status === 'UNVERIFIED') {
    if (!isCodeVerified(phone)) {
      return {
        canSend: false,
        code: 'needs_verification',
        reason: 'This number has not been verified with Meta yet. Request a verification ' +
          'code, enter it, and then register the number with its PIN.'
      };
    }

    return {
      canSend: false,
      code: 'not_registered',
      reason: 'This number still needs to be registered with a 6-digit PIN before it can send.'
    };
  }

  if (status === 'DISCONNECTED' || status === 'MIGRATED' || status === 'BANNED') {
    return {
      canSend: false,
      code: 'disconnected',
      reason: `This number is ${status.toLowerCase()} on Meta and cannot send messages.`
    };
  }

  return {
    canSend: false,
    code: 'unknown_status',
    reason: `Meta reports an unrecognised status (${status}) for this number.`
  };
}

/**
 * Boolean shorthand for evaluateSendability, suitable for Array.filter.
 */
export function canSendFrom(phone: SendablePhoneLike | null | undefined): boolean {
  return evaluateSendability(phone).canSend;
}

/** The onboarding call a number still owes, or null when it owes none. */
export type PhoneSetupStep = 'verify' | 'register';

/**
 * Which of Meta's two setup calls this number needs next.
 *
 * Bringing a number online is two separate exchanges and they are easy to confuse,
 * because both take a six-digit number:
 *
 *   1. request_code then verify_code — proves the business owns the number, using an
 *      OTP Meta sends to it. Sets codeVerificationStatus.
 *   2. register — puts the number on Cloud API, using the account's own two-step PIN.
 *      Sets status to CONNECTED.
 *
 * Step 2 fails while step 1 is outstanding, which is what a migrated number hits: it
 * arrives from its old WABA needing a fresh OTP, and offering "Register Now" there
 * produced Meta's opaque parameter error instead of the OTP prompt it needed.
 */
export function phoneSetupStep(
  phone: SendablePhoneLike | null | undefined
): PhoneSetupStep | null {
  // A Coexistence number is already registered — it came in live from the
  // customer's WhatsApp Business app, and Meta never issues it an ownership
  // OTP or a two-step PIN. It can still read PENDING for a moment after
  // onboarding, so this is settled before any status reasoning: offering
  // Verify or Register here walks the customer into request_code/register
  // calls that can only fail, on a number that is working in their hand.
  if (phone?.isOnBizApp) return null;

  const verdict = evaluateSendability(phone);

  if (verdict.code === 'needs_verification') return 'verify';
  if (verdict.code === 'not_registered') return 'register';

  // Anything else is either sendable or broken in a way no setup call fixes.
  return null;
}

/**
 * Human-readable label for Meta's platform status, for status badges.
 */
export function phoneStatusLabel(phone: SendablePhoneLike | null | undefined): string {
  const status = typeof phone?.status === 'string' ? phone.status.toUpperCase() : '';

  switch (status) {
    case 'CONNECTED': return 'Connected';
    case 'FLAGGED': return 'Flagged';
    case 'RESTRICTED': return 'Restricted';
    case 'RATE_LIMITED': return 'Rate limited';
    // PENDING and UNVERIFIED both used to read "Pending Registration", which sent
    // people to the PIN step when what the number actually owed was an OTP. The
    // badge now names the step that is outstanding.
    case 'PENDING':
    case 'UNVERIFIED':
      return phoneSetupStep(phone) === 'verify' ? 'Pending Verification' : 'Pending Registration';
    case 'DISCONNECTED': return 'Disconnected';
    case 'MIGRATED': return 'Migrated';
    case 'BANNED': return 'Banned';
    default:
      // Legacy records with no stored status.
      return phone?.isConnected ? 'Connected' : 'Pending Registration';
  }
}

/**
 * Bootstrap badge class matching phoneStatusLabel.
 */
export function phoneStatusBadgeClass(phone: SendablePhoneLike | null | undefined): string {
  const verdict = evaluateSendability(phone);
  if (verdict.canSend) return 'bg-success';
  return verdict.temporary ? 'bg-warning text-dark' : 'bg-danger';
}

/**
 * Label for the informational code-verification column.
 * Purely descriptive — it never affects whether the number can send.
 */
export function codeVerificationLabel(
  value: 'VERIFIED' | 'NOT_VERIFIED' | 'EXPIRED' | string | null | undefined
): string {
  switch (value) {
    case 'VERIFIED': return 'Verified';
    case 'NOT_VERIFIED': return 'Not verified';
    case 'EXPIRED': return 'Expired';
    default: return 'Unknown';
  }
}

/** A phone record's most important problem, for the collapsed list row. */
export interface PhoneAlert {
  /** Drives the badge colour. */
  level: 'danger' | 'warning';
  /** Two or three words — this sits inline next to the number's name. */
  label: string;
  /** The full explanation, for the tooltip. */
  detail: string;
}

/** The fields an alert can be derived from. */
export interface AlertablePhoneLike extends SendablePhoneLike {
  nameChangeStatus?: string | null;
  pendingVerifiedName?: string | null;
  nameChangeRejectionReason?: string | null;
  qualityRating?: string | null;
}

/**
 * The single most important thing wrong with a phone number, if anything is.
 *
 * These warnings previously lived only inside the expanded row, so choosing a
 * number to send from meant expanding each one to discover that its name change
 * was pending or its quality had dropped. One badge on the collapsed row makes
 * the problem visible where the decision is actually made.
 *
 * Returns only the most severe, deliberately: a row is a row, and a number with
 * three problems still only needs the user to look once.
 */
export function phoneAlert(phone: AlertablePhoneLike | null | undefined): PhoneAlert | null {
  if (!phone) return null;

  // Cannot send at all — nothing else matters as much.
  const sendability = evaluateSendability(phone);
  if (!sendability.canSend) {
    // A number mid-setup is not broken, it is unfinished, and saying which step it
    // is on is more use on a collapsed row than a flat "Cannot send".
    const label = sendability.temporary ? 'Paused' :
      sendability.code === 'needs_verification' ? 'Needs verification' :
        sendability.code === 'not_registered' ? 'Needs registration' :
          'Cannot send';

    return {
      level: sendability.temporary ? 'warning' : 'danger',
      label,
      detail: sendability.reason || 'This number cannot currently send messages.'
    };
  }

  if (phone.nameChangeStatus === 'rejected') {
    return {
      level: 'danger',
      label: 'Name rejected',
      detail: phone.nameChangeRejectionReason ||
        'Meta rejected the requested display name for this number.'
    };
  }

  if (phone.nameChangeStatus === 'pending_review') {
    return {
      level: 'warning',
      label: 'Name pending',
      detail: phone.pendingVerifiedName ?
        `Meta is reviewing a change of display name to "${phone.pendingVerifiedName}".` :
        'Meta is reviewing a display name change for this number.'
    };
  }

  // Quality is a warning rather than a block: a RED number still sends, but
  // Meta may restrict it, and that is worth seeing before picking it.
  if (phone.qualityRating === 'RED') {
    return {
      level: 'danger',
      label: 'Low quality',
      detail: 'Meta has rated this number low quality. Its messaging limit may be reduced.'
    };
  }

  if (phone.qualityRating === 'YELLOW') {
    return {
      level: 'warning',
      label: 'Medium quality',
      detail: 'Meta has rated this number medium quality. Watch for blocks and reports.'
    };
  }

  return null;
}

/**
 * Meta's messaging tiers, in the raw form the webhook stores them.
 *
 * The full set is defined server-side in pubsub/metaWebhookProcessor.ts. It
 * matters that TIER_1K is in here: four separate copies of this mapping grew up
 * across the app — in the topbar, organisation settings, the dashboard and the
 * send-to-all modal — and every one of them omitted it, so a number on the
 * 1,000-conversation tier fell through to a generic fallback everywhere.
 */
const TIER_LABELS: Record<string, string> = {
  TIER_250: 'Tier 250',
  TIER_1K: 'Tier 1K',
  TIER_2K: 'Tier 2K',
  TIER_10K: 'Tier 10K',
  TIER_100K: 'Tier 100K',
  TIER_UNLIMITED: 'Unlimited'
};

/**
 * A readable name for a messaging tier — "Tier 1K" rather than "TIER_1K".
 *
 * Unknown values are reshaped rather than dropped: Meta has added tiers before
 * and will again, and a slightly awkward "Tier 500K" is far better than a blank
 * where a limit should be.
 */
export function tierLabel(tier: string | null | undefined): string {
  if (!tier) return '';
  return TIER_LABELS[tier] ?? tier.replace('TIER_', 'Tier ').replace(/_/g, ' ');
}

/**
 * How many conversations a tier allows per day, as a readable string.
 */
export function tierAllowance(tier: string | null | undefined): string {
  switch (tier) {
    case 'TIER_250': return '250 / day';
    case 'TIER_1K': return '1,000 / day';
    case 'TIER_2K': return '2,000 / day';
    case 'TIER_10K': return '10,000 / day';
    case 'TIER_100K': return '100,000 / day';
    case 'TIER_UNLIMITED': return 'Unlimited';
    default: return '';
  }
}

/**
 * Meta's GREEN/YELLOW/RED quality rating in plain words.
 *
 * The colour is what the API returns, but "RED" on its own does not tell a
 * support engineer that the number is close to being throttled.
 */
export function qualityLabel(rating: string | null | undefined): string {
  switch ((rating ?? '').toUpperCase()) {
    case 'GREEN': return 'Good';
    case 'YELLOW': return 'Medium';
    case 'RED': return 'Low';
    default: return 'Unrated';
  }
}

/** Bootstrap badge class for a quality rating. */
export function qualityBadgeClass(rating: string | null | undefined): string {
  switch ((rating ?? '').toUpperCase()) {
    case 'GREEN': return 'bg-success';
    case 'YELLOW': return 'bg-warning';
    case 'RED': return 'bg-danger';
    default: return 'bg-secondary';
  }
}
